
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';

import axios from 'axios';

const API_URL = 'auth/login/';

export const loginUser = createAsyncThunk(
    'auth/loginUser',
    async ({ email, password }, thunkAPI) => {
        try {
            const response = await axios.post(API_URL, {
                email,
                password,
            });

            return response.data?.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                error.response || 'Login failed'
            );
        }
    }
);

export const refreshAccessToken = createAsyncThunk(
    'auth/refreshAccessToken',
    async (_, thunkAPI) => {
        const { refreshToken } = thunkAPI.getState().auth;

        try {
            const response = await axios.post('auth/refresh/', {
                refresh_token: refreshToken,
            });

            return response.data?.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(
                error.response?.data?.message || 'Token refresh failed'
            );
        }
    }
);


export const updateCurrentRole = createAsyncThunk(
    'auth/updateCurrentRole',
    async (roleKey, thunkAPI) => {
        const { token } = thunkAPI.getState().auth;
        try {
            const apiRoleMapping = {
                supervisor: "supervisor",
                team_lead: "teamlead",
                telecaller: "telecaller",
                field_agent: "fieldagent",
                agency: "agency"
            };
            const roleForApi = apiRoleMapping[roleKey] || roleKey;

            const response = await axios.post(
                'auth/current-role/',
                { current_role_login: roleForApi },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                }
            );
            return response.data;
        } catch (error) {
            console.error('Update current role API failed:', error);
            return thunkAPI.rejectWithValue(
                error.response?.data?.message || 'Update current role failed'
            );
        }
    }
);

const initialState = {
    user: null,
    token: null,
    refreshToken: null,
    tokenExpiry: null,
    isLoading: false,
    isAuthenticated: false,
    error: null,
};

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        logout: (state) => {
            state.user = null;
            state.token = null;
            state.refreshToken = null;
            state.tokenExpiry = null;
            state.isAuthenticated = false;
            state.error = null;
        },
        setBreakStatus: (state, action) => {
            const { break_type, is_on_break } = action.payload;
            if (break_type === 'lunch') {
                state.isOnLunchBreak = is_on_break;
                if (is_on_break) {
                    state.inOnTeaBreak = false;
                }
            } else if (break_type === 'tea') {
                state.inOnTeaBreak = is_on_break;
                if (is_on_break) {
                    state.isOnLunchBreak = false;
                }
            }
        },
        setSelectedRole: (state, action) => {
            if (state.user) {
                state.user.selectedRole = action.payload;
                state.user.is_supervisor = action.payload === 'supervisor';
                state.user.is_tl = action.payload === 'team_lead';
                state.user.is_tc = action.payload === 'telecaller';
                state.user.is_fa = action.payload === 'field_agent';
                state.user.is_agency = action.payload === 'agency';
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(loginUser.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(loginUser.fulfilled, (state, action) => {
                const expiresInSeconds = parseInt(action.payload.expiresIn, 10);
                const expiryTime = Date.now() + expiresInSeconds * 1000;
                state.isLoading = false;
                state.user = action.payload;
                state.token = action.payload.idToken;
                state.refreshToken = action.payload.refreshToken;
                state.tokenExpiry = expiryTime;
                state.isAuthenticated = true;
                state.error = null;
            })
            .addCase(loginUser.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload || 'Something went wrong';
            })

            .addCase(refreshAccessToken.fulfilled, (state, action) => {
                const expiresInSeconds = parseInt(action.payload.expiresIn, 10);
                const expiryTime = Date.now() + expiresInSeconds * 1000;
                state.token = action.payload.idToken;
                state.tokenExpiry = expiryTime;
                state.isAuthenticated = true;
                state.refreshToken = action.payload.refreshToken
            })
            .addCase(refreshAccessToken.rejected, (state) => {
                state.token = null;
                state.refreshToken = null;
                state.tokenExpiry = null;
                state.user = null;
                state.isAuthenticated = false;
                state.user = null
            })

    },
});

export const { logout, setBreakStatus, setSelectedRole } = authSlice.actions;
export default authSlice.reducer;
